import req from '../../api/request'
import type { SnEvent } from '../../features/event/types'

export interface EventPagePagination {
  current_page: number
  total_pages: number
}

export interface EventPage {
  data: SnEvent[]
  /** Null when the response carried no usable pagination — one page, complete. */
  pagination: EventPagePagination | null
}

interface EventPageResponse {
  data?: SnEvent[]
  meta?: { pagination?: Record<string, unknown> }
}

/**
 * ONE page of an event list, with its pagination intact.
 *
 * The only way the app reads event lists. An earlier fetcher drained every page
 * before returning, because every consumer then treated events as complete; the
 * schedule lists now page as the user scrolls, merging against the games list's
 * own pagination, so they need the `meta` that fetcher discarded.
 *
 * Unusable pagination reads as COMPLETE rather than as "keep going" — a bare
 * array, a missing `meta`, a non-numeric `total_pages`. The two failure modes
 * are not symmetric: stopping early renders a short list the user can still
 * see, while looping on a server that ignores `page` never terminates.
 *
 * Accepts both the documented envelope and a bare array, like every other event
 * list service — the endpoints return each and this tolerates whichever ships.
 *
 * Errors throw, like every other event service.
 */
export const fetchEventPage = async <TQuery extends object>(
  path: string,
  query: TQuery = {} as TQuery,
  page = 1
): Promise<EventPage> => {
  const response = await req(path, { query: { ...query, page } })

  // A bare array carries no pagination, so it is the whole answer.
  if (Array.isArray(response)) {
    return { data: response as SnEvent[], pagination: null }
  }

  const envelope = response as EventPageResponse
  const raw = envelope?.meta?.pagination
  const currentPage = Number(raw?.current_page ?? page)
  const totalPages = Number(raw?.total_pages ?? 1)
  const isUsable = Number.isFinite(currentPage) && Number.isFinite(totalPages)

  return {
    data: (envelope?.data ?? []) as SnEvent[],
    pagination: isUsable
      ? { current_page: currentPage, total_pages: totalPages }
      : null,
  }
}
